import React, { useCallback, useEffect, useRef, useState } from 'react';

const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const LOWER = 'abcdefghijklmnopqrstuvwxyz';

const randomCharacter = (character) => {
  if (typeof character !== 'string') return '';
  if (!/[a-z]/i.test(character)) return character;
  const characters = character === character.toUpperCase() ? UPPER : LOWER;
  return characters[Math.floor(Math.random() * characters.length)];
};

const scrambleFrame = (text, progress) => {
  const letters = [...text];
  const letterIndexes = letters
    .map((character, index) => (/[a-z]/i.test(character) ? index : -1))
    .filter((index) => index !== -1);
  if (progress >= 1 || letterIndexes.length === 0) return text;

  const safeProgress = Math.max(0, Math.min(Number.isFinite(progress) ? progress : 0, 1));
  const lockedCount = Math.floor(letterIndexes.length * safeProgress);
  const activeLetter = Math.max(0, Math.min(lockedCount, letterIndexes.length - 1));
  const activeIndex = letterIndexes[activeLetter];

  return `${letters.slice(0, activeIndex).join('')}${randomCharacter(letters[activeIndex])}`;
};

function ScrambleText({ text, className = '', onComplete }) {
  const elementRef = useRef(null);
  const frameRef = useRef(null);
  const isVisibleRef = useRef(false);
  const [displayText, setDisplayText] = useState(text);

  const play = useCallback((duration = 800) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setDisplayText(text);
      onComplete?.();
      return;
    }

    cancelAnimationFrame(frameRef.current);
    const start = performance.now();

    const animate = (now) => {
      const elapsed = Math.max(0, Math.min((now - start) / duration, 1));
      setDisplayText(scrambleFrame(text, elapsed));

      if (elapsed < 1) frameRef.current = requestAnimationFrame(animate);
      else {
        setDisplayText(text);
        onComplete?.();
      }
    };

    frameRef.current = requestAnimationFrame(animate);
  }, [onComplete, text]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !isVisibleRef.current) {
        isVisibleRef.current = true;
        play();
      } else if (!entry.isIntersecting) {
        isVisibleRef.current = false;
        cancelAnimationFrame(frameRef.current);
        setDisplayText(text);
      }
    }, { threshold: 0 });

    observer.observe(element);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frameRef.current);
    };
  }, [play, text]);

  return (
    <span
      ref={elementRef}
      className={`scramble-text ${className}`.trim()}
      aria-label={text.replace(/\n/g, ' ')}
    >
      <span aria-hidden="true">{displayText}</span>
    </span>
  );
}

export default ScrambleText;
